import { isSuccess } from '@devexperts/remote-data-ts'
import { init, MapCanvasApi, Size } from '@spiaggeit/map-canvas'
import { Color } from '@spiaggeit/spit-core'
import { MutableRefObject, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import { useDebounceCallback } from 'usehooks-ts'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { usePrevious } from '@/hooks/usePrevious'
import { BoundingBox2d, MapElementWithSectorId } from '@/models/map'
import { ServiceInfoPurchaseType } from '@/models/service'
import { cartSlice } from '@/store/cartSlice'
import type { RootState } from '@/store/configureStore'
import { licenseSlice } from '@/store/licenseSlice'
import { mapSizeSelector } from '@/store/licenseSlice/selectors/mapSizeSelector'
import { mapConfigSlice } from '@/store/mapConfigSlice'
import {
  loadDecorationCategories,
  loadMapData,
  loadSectors,
  mapSelectors,
  mapSlice,
} from '@/store/mapSlice'
import { configSelector } from '@/store/selectors/configSelector'
import { servicesSlice } from '@/store/servicesSlice'
import {
  MAP_CELL_SIZE,
  USER_HAS_VISITED_MAP_BEFORE_KEY,
} from '@/utils/constants'
import {
  computeWarehouseAdjustedMapElements,
  getAvailableMapSpots,
} from '@/utils/map/applyWarehouseMapAvailability'

function calculateInitialZoom(
  containerRect: { width: number; height: number },
  mapQuadrant: BoundingBox2d
) {
  const quadrantWidth = mapQuadrant.max.x - mapQuadrant.min.x
  const quadrantHeight = mapQuadrant.max.y - mapQuadrant.min.y
  const scaleX = containerRect.width / quadrantWidth
  const scaleY = containerRect.height / quadrantHeight
  return Math.min(scaleX, scaleY)
}

export function useMapUtils(canvasEl: MutableRefObject<HTMLDivElement | null>) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()

  const [isCanvasReady, setIsCanvasReady] = useState(false)

  const canvasAPIRef = useRef<MapCanvasApi | null>(null)
  const cartItemsIdsRef = useRef<string[]>([])

  const config = useAppSelector(configSelector)
  const mapData = useAppSelector(mapSelectors.mapData)
  const mapLoading = useAppSelector(mapSlice.selectors.mapLoading)
  const cartItemsIds = useAppSelector(cartSlice.selectors.itemsIds)
  const mapConfig = useAppSelector(mapConfigSlice.selectors.config)
  const mapSize = useAppSelector(mapSizeSelector)
  const license = useAppSelector(licenseSlice.selectors.license)
  const warehouse = useAppSelector(mapSlice.selectors.warehouse)

  const previousCartItemsIds = usePrevious(cartItemsIds)

  cartItemsIdsRef.current = [...cartItemsIds]

  const handleMapElementClickRef = useRef<(elementId: string) => void>(() => {})

  handleMapElementClickRef.current = (elementId: string) => {
    const elements =
      computeWarehouseAdjustedMapElements(store.getState()) ??
      mapSelectors.mapData(store.getState())?.elements

    if (!elements) {
      return
    }

    const element = elements.find((item) => item.id == elementId)

    if (!element?.style) {
      return
    }

    if (element.style.iconColor === Color.GREY) {
      return
    }

    dispatch(cartSlice.actions.setCartPopoverIsOpen(false))
    dispatch(mapSlice.actions.selectMapElement(element))
  }

  function syncWarehouseMapDisplay() {
    if (!isSuccess(warehouse)) {
      return
    }

    const elements = computeWarehouseAdjustedMapElements(store.getState())
    if (!elements) {
      return
    }

    dispatch(mapSlice.actions.updateMapElements(elements))
    setAvailableSpots(getAvailableMapSpots(elements))

    if (canvasAPIRef.current) {
      void canvasAPIRef.current.renderElements(elements)
    }
  }

  const resetCanvasHeightHandler = useDebounceCallback(() => {
    const firstChild = document.getElementById('canvas')
      ?.firstChild as HTMLDivElement

    if (!firstChild) return

    firstChild.style.height = ''
  }, 100)

  const [availableSpots, setAvailableSpots] = useState<
    MapElementWithSectorId[]
  >([])

  const userHasVisitedMapBefore = localStorage.getItem(
    USER_HAS_VISITED_MAP_BEFORE_KEY
  )

  function clearMapLoading() {
    if (!mapLoading) {
      return
    }
    const elapsed = Date.now() - mapLoading.timestamp

    if (elapsed < 500) {
      setTimeout(() => {
        dispatch(mapSlice.actions.setIsMapLoading(null))
      }, 150)
    } else {
      dispatch(mapSlice.actions.setIsMapLoading(null))
    }
  }

  function getMapSize(): Size {
    if (!license || !canvasEl.current) {
      return {
        height: 0,
        width: 0,
      }
    }

    return {
      height: license.mapBg
        ? mapSize.height
        : Math.max(mapSize.height, canvasEl.current.clientHeight),
      width: license.mapBg
        ? mapSize.width
        : Math.max(mapSize.width, canvasEl.current.clientWidth),
    }
  }

  return {
    availableSpots: {
      areMoreThanOne: availableSpots && availableSpots.length > 1,
      areNone: availableSpots && availableSpots.length < 1,
      array: availableSpots,
      isOnlyOne: availableSpots && availableSpots.length === 1,
    },
    getCanvasAPI: () => {
      if (!canvasAPIRef.current) {
        throw new Error('Map canvas API is not available.')
      }

      return canvasAPIRef.current
    },
    highlightSelectedItems: function () {
      try {
        if (!mapData?.elements) {
          throw new Error('Map elements are not available.')
        }
        const api = this.getCanvasAPI()

        const newElements = cartItemsIds.filter(
          (element) => !previousCartItemsIds?.includes(element)
        )
        const removedElements =
          previousCartItemsIds?.filter(
            (element) => !cartItemsIds.includes(element)
          ) ?? []

        // when they match, we took the last value because it should match the initial render
        if (newElements.length === 0 && removedElements.length === 0) {
          const promises = cartItemsIds.map((id) =>
            api.highlightElement(id, Color.BLUE)
          )
          Promise.allSettled(promises).finally(() => {
            clearMapLoading()
          })
        } else {
          if (removedElements.length > 0) {
            api.clearHighlight(removedElements).then(() => {
              clearMapLoading()
            })
          }

          if (newElements.length > 0) {
            const promises = newElements.map((id) =>
              api.highlightElement(id, Color.BLUE)
            )
            Promise.allSettled(promises).finally(() => {
              clearMapLoading()
            })
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    },
    initMap: async function () {
      if (!mapData || !mapData.elements || !license) {
        throw new Error('Required map data is not available.')
      }

      const realMapSize = getMapSize()

      // Default to map center if for some reason we can't find the best quadrant
      // Happens when there are no available spots
      const initialPosition = mapData.assetsBaseUrl
        ? {
            x: realMapSize.width / 2,
            y: realMapSize.height / 2,
          }
        : {
            x: 0,
            y: 0,
          }

      const elements =
        computeWarehouseAdjustedMapElements(store.getState()) ??
        mapData.elements ??
        []
      let initialZoom = undefined
      const container = document.getElementById('canvas')!
      const boundingClientRect = container.getBoundingClientRect()
      // If we identified the best quadrant, we override the initial map center position
      if (mapData.bestQuadrant != null) {
        initialPosition.x = mapData.bestQuadrant.center.x
        initialPosition.y = mapData.bestQuadrant.center.y
        initialZoom = calculateInitialZoom(
          boundingClientRect,
          mapData.bestQuadrant
        )
      }

      dispatch(mapSlice.actions.updateMapElements(elements))

      canvasAPIRef.current = await init({
        config: mapConfig,
        events: {
          onMapElementClick(elementId) {
            handleMapElementClickRef.current(elementId)
          },
          onMapPanZoom() {
            canvasAPIRef.current?.updateClusterVisibility()
          },
        },
        opts: {
          assetsBaseUrl: `${config.assetsBase}/booking/map`,
          backgroundUrl: license.mapBg,
          backgroundUrl2: license.mapBg2,
          containerId: 'canvas',
          elements,
          initialPosition: {
            x: -initialPosition.x,
            y: -initialPosition.y,
          },
          initialZoom: initialZoom,
          originalSize: mapSize,
          size: realMapSize,
        },
      })

      if (mapData.bestQuadrant != null) {
        // Center canvas on position
        const stageInfo = canvasAPIRef.current.getStageInfo()
        initialPosition.x -= stageInfo.rect.width / 2
        initialPosition.y -= stageInfo.rect.height / 2
        canvasAPIRef.current.goToPosition({
          x: -initialPosition.x,
          y: -initialPosition.y,
        })
      }

      setAvailableSpots(getAvailableMapSpots(elements))

      setIsCanvasReady(true)

      this.highlightSelectedItems()
    },
    isCanvasReady,
    loadInitialData: async function () {
      try {
        await dispatch(loadDecorationCategories()).unwrap()

        dispatch(
          loadMapData({
            cellSize: MAP_CELL_SIZE,
            screenHeight: window?.innerHeight ?? 0,
            screenWidth: window?.innerWidth ?? 0,
          })
        )
        dispatch(loadSectors())
        dispatch(
          servicesSlice.actions.load({
            purchaseType: ServiceInfoPurchaseType.WITH_SPOT,
          })
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load data:', error)
      }
    },
    resetCanvasHeightHandler,
    syncWarehouseMapDisplay,
    userHasVisitedMapBefore,
    zoomIn: function () {
      canvasAPIRef.current?.zoomIn()
    },
    zoomOut: function () {
      canvasAPIRef.current?.zoomOut()
    },
  }
}
